<template>
    <!-- Onglet de parametrage de l'utilisateur [Saisie/Patrimoine/Dossier Partagé]  -->
    <b-tabs card content-class="flex-grow-1 overflow-auto bg-white mx-2 px-2" :active-nav-item-class="`text-${Variant}`" nav-class="p-0"
            class="d-flex flex-column h-100 mh-100 w-100 px-4 mb-4 overflow-auto">
        <template #tabs-end>
            <b-row class="flex-grow-1 justify-content-end">
                <b-col cols="auto" align-self="center">
                    <!-- Barre d'actions sur l'utilisateur [annuler/sauvegarde]-->
                    <b-button-toolbar key-nav :aria-label="$t('ToolbarButtons')">
                        <b-button-group>
                            <b-button @click="onSubmitCancel()" :variant="'outline-' + Variant" v-b-tooltip.noninteractive.hover :title="$t('Cancel')"
                                :disabled="!$store.getters['CheckAction']('Read', undefined, Action)"
                                class="btn-circle p-0">
								<i class="bi-ban" />
							</b-button>
                        </b-button-group>
                        
                        
                        <b-button-group v-if="$store.getters['CheckAction']('Write', undefined, Action) " class="mx-1">
                            <!-- $t('AddUser') / Ajouter un utilisateur -->
                            <b-avatar :title="$t('SaveUser')" :variant="Variant" button button-type="submit" :form="$id('userAddEdit')" badge-top 
                                :badge-variant="isWarnInErrorFields ? 'danger' : 'info'" badge-class="bg-transparent p-0" size="2rem"
                                :disabled="!$store.getters['CheckAction']('Write', undefined, Action)">
                                <template #default>
                                    <i class="bi bi-floppy" />
                                </template>
                                <template #badge v-if="warnUpdatedFields() || warnInErrorFields()">
                                    <!-- WarnUpdatedFields : Des champs ont été modifiés -->
                                    <b-icon v-if="isWarnUpdatedFields && !isWarnInErrorFields"
                                        v-b-tooltip.noninteractive.hover :title="$t('WarnUpdatedFields')" icon="exclamation" variant="light" />

                                    <!-- WarnInErrorFields : Certains champs comportent des erreurs-->
                                    <b-icon v-if="isWarnInErrorFields"
                                        v-b-tooltip.noninteractive.hover :title="$t('WarnInErrorFields')" icon="exclamation" variant="light" />
                                </template>
                            </b-avatar>
                        </b-button-group>
                    </b-button-toolbar>
                </b-col>
            </b-row>
        </template>
        
        <!-- Formulaire de saisie -->
        <b-tab>
            <template #title>
                <div class="d-inline-block inline-block position-relative bg-transparent border-0">
                    {{ $t('UserInfos') }}
                    <!-- ErrorForm : Ce formulaire comporte des erreurs -->
                    <b-icon v-if="$refs.userForm && $refs.userForm.errors && $refs.userForm.flags['validated'] 
                        && $refs.userForm.flags['invalid'] && Object.keys($refs.userForm.errors).length > 0"
                        v-b-tooltip.noninteractive.hover :title="$t('ErrorForm')" icon="exclamation-circle-fill" class="text-danger" role="button" />
                    <!-- InfoUpdatedForm : Des champs ont été modifiés dans ce formulaire -->
                    <b-icon v-else-if="isFormChanged" v-b-tooltip.noninteractive.hover :title="$t('InfoUpdatedForm')" icon="exclamation-circle-fill" class="text-info" role="button" />
                </div>
            </template>
            <b-row>
                <b-col>
                    <!-- Validateur du formulaire (fourni méthode handleSubmit pour la validation du formulaire) -->
                    <ValidationObserver ref="userForm" v-slot="{ handleSubmit , valid }" slim>
                        <b-form :id="$id('userAddEdit')" @submit.prevent="handleSubmit(onSubmitSuccess); onSubmitInvalid(valid); " novalidate> <!-- encapsule onSubmit dans la méthode de validation -->
                            <b-row>
                                <!-- Colonne formulaire -->
                                <b-col cols="9">
                                    <!-- Section Identité -->
                                    <b-row>
                                        <b-col>
                                            <b-card>
                                                <b-form-group
                                                        :label="$t('Identity')"
                                                        label-cols="12"
                                                        label-cols-lg="12"
                                                        label-class="font-weight-bold text-dark pt-0 pb-2"
                                                        label-align-sm="left"
                                                        label-size="lg" />

                                                <!-- Civilité -->
                                                <ValidationProvider v-slot="{ errors, valid, changed }" rules="max:256">
                                                    <b-form-group id="civility" label-for="civility-input" :label-cols="sizeOfLabel" :content-cols="sizeOfInput" class="mb-1" 
                                                        :invalid-feedback="errors[0]">
                                                        <template #label>
                                                                {{ $t('Civility') }}
                                                        </template>
                                                        <b-form-input id="civility-input" v-model="user.Civility" type="text"  required trim :state="getState(valid, changed, false)"/>
                                                    </b-form-group>
                                                </ValidationProvider>

                                                <!-- Nom -->
                                                <ValidationProvider v-slot="{ errors, valid, changed, validated }" rules="required|max:256">
                                                    <b-form-group id="lastName" label-for="lastName-input" :label-cols="sizeOfLabel" :content-cols="sizeOfInput" class="mb-1" 
                                                        :invalid-feedback="errors[0]">
                                                        <template #label>
                                                                {{ $t('LastName') }}<span class="font-weight-bold text-danger"> *</span>
                                                        </template>
                                                        <b-form-input id="lastName-input" v-model="user.LastName" type="text"  required trim :state="getState(valid, changed, validated)"/>
                                                    </b-form-group>
                                                </ValidationProvider>

                                                <!-- Prénom -->
                                                <ValidationProvider v-slot="{ errors, valid, changed, validated }" rules="required|max:256">
                                                    <b-form-group id="firstName" label-for="firstName-input" :label-cols="sizeOfLabel" :content-cols="sizeOfInput" class="mb-1"
                                                        :invalid-feedback="errors[0]">
                                                        <template #label>
                                                                {{ $t('FirstName') }}<span class="font-weight-bold text-danger"> *</span>
                                                        </template>
                                                        <b-form-input id="firstName-input" v-model="user.FirstName" type="text"  required trim :state="getState(valid, changed, validated)"/>
                                                    </b-form-group>
                                                </ValidationProvider>

                                                <!-- Organization -->
                                                <ValidationProvider v-slot="{ errors, valid, changed }" rules="max:256">
                                                    <b-form-group id="company" :label="$t('Organization')" label-for="company-input" :label-cols="sizeOfLabel" :content-cols="sizeOfInput" class="mb-1"
                                                        :invalid-feedback="errors[0]">
                                                        <b-form-input id="company-input" v-model="user.Company" type="text" trim :state="getState(valid, changed, false)"/>
                                                    </b-form-group>
                                                </ValidationProvider>

                                                <!-- Fonction -->
                                                <ValidationProvider v-slot="{ errors, valid, changed }" rules="max:256">
                                                    <b-form-group id="function" :label="$t('FunctionInCompany')" label-for="function-input" :label-cols="sizeOfLabel" :content-cols="sizeOfInput" class="mb-1"
                                                        :invalid-feedback="errors[0]" >
                                                        <b-form-input id="function-input" v-model="user.Function" type="text" :state="getState(valid, changed, false)"/>
                                                    </b-form-group>
                                                </ValidationProvider>

                                                <!-- Language -->
                                                <ValidationProvider v-slot="{ errors, valid, changed, validated }" rules="required">
                                                    <b-form-group id="language" label-for="language-input" :label-cols="sizeOfLabel" :content-cols="sizeOfInput" class="mb-1"
                                                        :invalid-feedback="errors[0]">
                                                        <template #label>
                                                            {{ $t('Language') }}<span class="font-weight-bold text-danger"> *</span>
                                                        </template>
                                                        <b-form-select id="language-input" v-model="user.Language" plain :options="languagesOptions" :state="getState(valid, changed, validated)"/>
                                                    </b-form-group>
                                                </ValidationProvider>

                                                <!-- Profil -->
                                                <ValidationProvider v-slot="{ errors, valid, changed, validated}" rules="required">
                                                    <b-form-group id="profil" label-for="profil-input" :label-cols="sizeOfLabel" :content-cols="sizeOfInput" class="mb-1"
                                                        :invalid-feedback="errors[0]">
                                                        <template #label>
                                                            {{ $t('Profile') }}<span class="font-weight-bold text-danger"> *</span>
                                                        </template>
                                                        <b-form-select id="profil-input" v-model="user.Profil" plain :options="profilesOptions" :state="getState(valid, changed, validated)"/>
                                                    </b-form-group>
                                                </ValidationProvider>
                                            </b-card>
                                        </b-col>
                                    </b-row>
                                    <!-- Section Connexion -->
                                    <b-row class="pt-4">
                                        <b-col>
                                            <b-card>
                                                <b-form-group
                                                        label="Connexion"
                                                        label-cols="12"
                                                        label-cols-lg="12"
                                                        label-class="font-weight-bold text-dark pt-0 pb-2"
                                                        label-align-sm="left"
                                                        label-size="lg" />

                                                <!-- Identifiant -->
                                                <ValidationProvider v-slot="{ errors, valid, changed, validated }" rules="required|noSpace|min:5|max:50">
                                                    <b-form-group id="login" label-for="login-input" :label-cols="sizeOfLabel" :content-cols="sizeOfInput" class="mb-1"
                                                        :invalid-feedback="errors[0]">
                                                        <template #label>
                                                            {{ $t('Login') }}<span class="font-weight-bold text-danger"> *</span>
                                                        </template>
                                                        <b-form-input :disabled="UserId > 1" id="login-input" v-model="user.Login" type="text" required :state="getState(valid, changed, validated)"/>
                                                    </b-form-group>
                                                </ValidationProvider>

                                                <!-- Authentification -->
                                                <ValidationProvider v-slot="{ errors, valid, changed, validated }" rules="required">
                                                    <b-form-group id="authMode" label-for="authMode-input" :label-cols="sizeOfLabel" :content-cols="sizeOfInput" class="mb-1"
                                                        :invalid-feedback="errors[0]">
                                                        <template #label>
                                                            {{ $t('AuthMode') }}<span class="font-weight-bold text-danger"> *</span>
                                                        </template>
                                                        <b-form-radio-group plain id="authMode-input" v-model="user.AuthMode" class="pt-1" >
                                                            <b-form-radio plain value="basic">{{$t('Password')}}</b-form-radio>
                                                            <b-form-radio plain value="sso">{{ $t('SSO') }}</b-form-radio>
                                                        </b-form-radio-group>
                                                    </b-form-group>
                                                </ValidationProvider>

                                                <!-- SSO -->
                                                <ValidationProvider v-if="user.AuthMode === 'sso'" v-slot="{ errors, valid, changed, validated}" rules="required">
                                                    <b-form-group id="sso" label-for="sso-input" :label-cols="sizeOfLabel" :content-cols="sizeOfInput" class="mb-1"
                                                        :invalid-feedback="errors[0]">
                                                        <template #label>
                                                            {{ $t('SSO') }}<span class="font-weight-bold text-danger"> *</span>
                                                        </template>
                                                        <b-form-select id="sso-input" v-model="user.SsoAuthorityRef" plain :options="ssoOptions" :state="getState(valid, changed, validated)"/>
                                                    </b-form-group>
                                                </ValidationProvider>

                                                <!-- Email -->
                                                <ValidationProvider v-slot="{ errors, valid, changed, validated }" rules="required|noSpace|email|max:50">
                                                    <b-form-group id="email" label-for="email-input" :label-cols="sizeOfLabel" :content-cols="sizeOfInput" class="mb-1"
                                                        :invalid-feedback="errors[0]">
                                                        <template #label>
                                                            {{ $t('Email') }}<span class="font-weight-bold text-danger"> *</span>
                                                        </template>
                                                        <b-form-input id="email-input" v-model="user.Email" type="email" required  :state="getState(valid, changed, validated)"/>
                                                    </b-form-group>
                                                </ValidationProvider>

                                                <!-- Telephone Fixe -->
                                                <ValidationProvider v-slot="{ errors, valid, changed }" rules="phoneNumber">
                                                    <b-form-group id="telNumber" :label="$t('LandlinePhone')" label-for="telNumber-input" :label-cols="sizeOfLabel" :content-cols="sizeOfInput" class="mb-1"
                                                        :invalid-feedback="errors[0]" >
                                                        <b-form-input id="telNumber-input" v-model="user.TelNumber" :state="getState(valid, changed, false)"/> 
                                                    </b-form-group>
                                                </ValidationProvider>
                                                
                                                <!-- Portable -->
                                                <ValidationProvider v-slot="{ errors, valid, changed }" rules="phoneNumber">
                                                    <b-form-group id="portNumber" :label="$t('Mobile')" label-for="portNumber-input" :label-cols="sizeOfLabel" :content-cols="sizeOfInput" class="mb-1"
                                                        :invalid-feedback="errors[0]" >
                                                        <b-form-input id="portNumber-input" v-model="user.PortNumber" :state="getState(valid, changed, false)"/>
                                                    </b-form-group>
                                                </ValidationProvider>

                                                <!-- Fax -->
                                                <ValidationProvider v-slot="{ errors, valid, changed }" rules="phoneNumber">
                                                    <b-form-group id="faxNumber" :label="$t('FaxNumber')" label-for="faxNumber-input" :label-cols="sizeOfLabel" :content-cols="sizeOfInput" class="mb-1"
                                                        :invalid-feedback="errors[0]">
                                                        <b-form-input id="faxNumber-input" v-model="user.FaxNumber" :state="getState(valid, changed, false)"/>
                                                    </b-form-group>
                                                </ValidationProvider>
                                                
                                            </b-card>
                                        </b-col>
                                    </b-row>
                                    <!-- Section Localisation -->
                                    <b-row class="pt-4">
                                        <b-col>
                                            <b-card>
                                                <b-form-group
                                                        label="Localisation"
                                                        label-cols="12"
                                                        label-cols-lg="12"
                                                        label-class="font-weight-bold text-dark pt-0 pb-2"
                                                        label-align-sm="left"
                                                        label-size="lg" />
                                                <!-- Adresse -->
                                                <ValidationProvider v-slot="{ errors, valid, changed }" rules="max:256">
                                                    <b-form-group id="address" :label="$t('Address')" label-for="address-input" :label-cols="sizeOfLabel" :content-cols="sizeOfInput" class="mb-1"
                                                        :invalid-feedback="errors[0]">
                                                        <b-form-input id="address-input" v-model="user.Address" type="text" :state="getState(valid, changed, false)"/>
                                                    </b-form-group>
                                                </ValidationProvider>
                                                
                                                <!-- Code postale -->
                                                <ValidationProvider v-slot="{ errors, valid, changed }" rules="max:256">
                                                    <b-form-group id="postalCode" :label="$t('PostalCode')" label-for="postalCode-input" :label-cols="sizeOfLabel" :content-cols="sizeOfInput" class="mb-1"
                                                        :invalid-feedback="errors[0]">
                                                        <b-form-input id="postalCode-input" v-model="user.PostalCode" type="text" :state="getState(valid, changed, false)"/>
                                                    </b-form-group>
                                                </ValidationProvider>
                                                
                                                <!-- Ville -->
                                                <ValidationProvider v-slot="{ errors, valid, changed }" rules="max:256">
                                                    <b-form-group id="town" :label="$t('City')" label-for="town-input" :label-cols="sizeOfLabel" :content-cols="sizeOfInput" class="mb-1"
                                                        :invalid-feedback="errors[0]">
                                                        <b-form-input id="town-input" v-model="user.Town" type="text" :state="getState(valid, changed, false)"/>
                                                    </b-form-group>
                                                </ValidationProvider>
                                                
                                                <!-- Pays -->
                                                <ValidationProvider v-slot="{ errors, valid, changed }" rules="max:256">
                                                    <b-form-group id="country" :label="$t('Country')" label-for="country-input" :label-cols="sizeOfLabel" :content-cols="sizeOfInput" class="mb-1"
                                                        :invalid-feedback="errors[0]">
                                                        <b-form-select id="country-input" v-model="user.Country" plain :options="countriesOptions" :state="getState(valid, changed, false)"/>
                                                    </b-form-group>
                                                </ValidationProvider>
                                            </b-card>
                                        </b-col>
                                    </b-row>
                                </b-col>
                                <!-- Colonne "Aide contextuelle" + Remarques -->
                                <b-col cols="3">
                                    <!-- Aide contextuelle -->
                                    <b-row>
                                        <b-col>
                                            <b-card>
                                                <p><b-icon icon="question-circle"/><span class="pl-2 font-weight-semibold">{{ $t(('ContextualHelp')) }}</span></p>
                                                <p>{{ $t('ContextualHelp1') }}</p>
                                                <p class="text-danger">{{ $t('ContextualHelp2') }}</p>
                                            </b-card>
                                        </b-col>
                                    </b-row>
                                    <!-- Section remarques -->
                                    <b-row class="pt-4">
                                        <b-col>
                                            <b-card>
                                                <!-- Commentaires -->
                                                <ValidationProvider v-slot="{ errors, valid, changed }" rules="max:4000">
                                                    <b-form-group id="comment" :label="$tc('Remark', 2)" label-for="comment-input"
                                                        label-cols="12"
                                                        label-cols-lg="12"
                                                        label-class="font-weight-bold text-dark pt-0 pb-2"
                                                        label-align-sm="left"
                                                        label-size="lg"
                                                        :invalid-feedback="errors[0]">
                                                        <b-form-textarea id="comment-input" v-model="user.Comment" rows="26" max-rows="27" :state="getState(valid, changed, false)"/>
                                                    </b-form-group>
                                                </ValidationProvider>
                                            </b-card>
                                        </b-col>
                                    </b-row>
                                </b-col>
                            </b-row>
                        </b-form>
                    </ValidationObserver>
                </b-col>
            </b-row>
        </b-tab>

        <!-- Saisie patrimoines -->
        <b-tab :lazy="!this.loadLazyParts.includes('NodeSelector')" class="mh-100 h-100 w-100 p-0 bg-gray-light" >
            <template #title>
                <div class="d-inline-block inline-block position-relative bg-transparent border-0">{{ $t('UserAsset') }}
                    <!-- InfoUpdatedForm : Des champs ont été modifiés dans ce formulaire -->
                    <b-icon v-if="isAssetChanged" v-b-tooltip.noninteractive.hover :title="$t('InfoUpdatedForm')" icon="exclamation-circle-fill" class="text-info" role="button" />
                </div>
            </template>
            <HeritageNodeSelector 
                @loaded="loadLazyParts.push('NodeSelector')"
                :SelectorTitle="$t('AvailableAssets')" :SelectorSubTitle="$t('SelectNodeForUser')" :PinsDisplayerTitle="$t('ConnectedAssets')"
                :IsModified="isAssetChanged" :FilterTreeStore="FilterTreeStore" :PinsSelected.sync="user.Pins" :Variant="Variant"/>
        </b-tab>

        <b-tab :lazy="!this.loadLazyParts.includes('FolderSelector')" class="mh-100 h-100 w-100 p-0 bg-gray-light" >
            <template #title>
                <div class="d-inline-block inline-block position-relative bg-transparent border-0">{{ $t('UserShareFolders') }}
                    <!-- InfoUpdatedForm : Des champs ont été modifiés dans ce formulaire -->
                    <b-icon v-if="isFolderChanged" v-b-tooltip.noninteractive.hover :title="$t('InfoUpdatedForm')" icon="exclamation-circle-fill" class="text-info" role="button" />
                </div>
            </template>
            <!-- Saisie dossiers partagés-->
            <UserFolderSelector @loaded="loadLazyParts.push('FolderSelector')" :IsModified="isFolderChanged" :Variant="Variant" :RenderTree="folderTree" :IdFolders.sync="user.Folders" :FilterTreeStore="FilterTreeStore"/>
        </b-tab>
    </b-tabs>
</template>

<script lang="ts">
/** Types vue.js */
import Vue from 'vue';
import Component from 'vue-class-component';
import { InjectReactive, Prop, Watch } from 'vue-property-decorator';
import { Getter } from 'vuex-class';
import cloneDeep from 'lodash/cloneDeep';

/** Variables d'environnements */
import { API_URL, SELECTOR_FILTER_TREE } from '@/App.settings';

/** Axios pour appels webAPI back-end */
import axios, { AxiosResponse } from 'axios';

/** Types personnalisés */
import Dictionary from '@/types/Dictionary';
import ListItem from '@/types/fields/ListItem';
import { GenericTree, GenericTreeNode } from '@/components/commons/Generics/GenericTree/GenericTreeClass';

/** Validation du formulaire */
import '@/vee-rules'
import { ValidationObserver, ValidationProvider } from 'vee-validate';

/**Fichier de traduction local */
import locales from './locales/UserAddEdit.json'

/** Composants enfants */
import UserFolderSelector from './UserAddEdit/UserFolderSelector.vue';
import { CompareUtils } from '@/components/commons/Utils';
import RegistrationTypeEnum from '@/enums/RegistrationTypeEnum';
import Pin from '@/types/FilterTree/Pin';
import HeritageNodeSelector from '@/components/commons/HeritageTree/HeritageNodeSelector.vue';

/** Composant de saisie ou modification des informations utilisateurs */
@Component({
    components: {
        ValidationProvider,
        ValidationObserver,
        HeritageNodeSelector,
        UserFolderSelector
	},
    i18n: { messages : locales}
})
export default class UserAddEdit extends Vue{
    /** Déclaration du type de la ref utilisé dans le template afin d'éviter un cast */
    $refs!: {
        /** Je ne trouve pas le type de la '$ref' 'ValidationObserver', je suis obligé d'utiliser 'any' par dépit. */
        userForm: any 
    }

    /**
     * Liste des onglets déjà chargé
     * @description Pour tracker les onglets qui ont déjà été ouvert et changer la prop Lazy à false si déjà ouvert ==> Evite que l'onglet se recharge à chaque réouverture */
    private loadLazyParts: string[] = [];
        
    /** Variant du menu */
    @InjectReactive()
    private Variant!: string;

    /** Droit disponible pour l'utilisateur en cours */
    @InjectReactive()
    public Action!: string;

    /** Id de l'utilisateur */
    @Prop({ default: () => -1 })
    public UserId : number;
    private userId : string;

    @Prop({ default: () => SELECTOR_FILTER_TREE + 'UserAddEdit'})
    public FilterTreeStore!: string;

    /** Getter pour obtenir une liste [languages, pays, profiles, ObjectType]*/
    @Getter('GetListItems')
    protected listItems!: (listName: string) => ListItem[];

    /** Liste des langues supportées par DCX */
    private languagesOptions: { value: Number, text: string }[] = [];

    /** Liste des pays stockés sur DCX */
    private countriesOptions: { value: Number, text: string }[] = [];

    /** Liste des profils disponibles pour cet utilisateur */
    private profilesOptions: { value: Number, text: string }[] = [];

    /** Liste des serveurs SSO disponible pour ce client */
    private ssoOptions: { value: Number, text: string }[] = []

    /** Arborescence de gestion documentaire */
    private folderTree: TreeFolderInformation = {
		Count: 0,
		Nodes: [],
		SelectedNodes: [],
        FlatNodes: {},
		CurrentSelectedNode: undefined
	}

    /** Données du formulaire */
    private user: UserInformation = {
        FirstName: "",
        LastName: "",
        Civility: "",
        Company: "",
        InCompany: "",
        Confirmation: "",
        LastVisit: "",
        RegistrationDate: "",
        Function: "",
        Language: "",
        Comment: "",
        Login: "",
        Password: "",
        Email: "",
        TelNumber: "",
        PortNumber: "",
        FaxNumber: "",
        Address: "",
        Town: "",
        PostalCode: "",
        Country: "",
        Profil: "",
        CommonGroups: [],
        Pins: [],
        Folders: [],
        AuthMode: "basic",
        SsoAuthorityRef: null
    }

    /** Donneés d'origine du formulaire */
    private userForCompare: UserInformation = {
        FirstName: "",
        LastName: "",
        Civility: "",
        Company: "",
        InCompany: "",
        Confirmation: "",
        LastVisit: "",
        RegistrationDate: "",
        Function: "",
        Language: "",
        Comment: "",
        Login: "",
        Password: "",
        Email: "",
        TelNumber: "",
        PortNumber: "",
        FaxNumber: "",
        Address: "",
        Town: "",
        PostalCode: "",
        Country: "",
        Profil: "",
        CommonGroups: [],
        Pins: [],
        Folders: [],
        AuthMode: "basic",
        SsoAuthorityRef: null
    }

    //#region CHANGE TRACKING
    /** Indique si le formulaire a été modifié */
    private isFormChanged = false;
    
    /** Indique si la liste des objets patrimoines a été modifié */
    private isAssetChanged = false;

    /** Indique si la liste des dossiers partagés a été modifié */
    private isFolderChanged = false;

    /** Watcher de détection de changement sur le formulaire */
    @Watch('user', { deep: true, immediate: true})
    private onUserFormChanged(){
        if(!CompareUtils.EqualsParams(this.userForCompare, this.user, ['Pins', 'Folders']))
            this.isFormChanged = true;
        //this.isFormChanged = !this.equalsParams(oldVal, newVal);
    }

    /** Watcher de détection de changement sur la liste d'éléments patrimoine */
    @Watch('user.Pins', { deep: true })
    private onUserAssetChanged(){
        console.debug("UserAddEdit - onUserAssetChanged() - this.userForCompare.Pins : %O - this.user.Pins : %O", this.userForCompare.Pins, this.user.Pins)
        if(!CompareUtils.EqualsParams(this.userForCompare.Pins, this.user.Pins, ['TreeNode']))
            this.isAssetChanged = true;
        //this.isAssetChanged = !this.equalsParams(this.userForCompare.Nodes, this.user.Nodes);
    }

    /** Watcher de détection de changement sur la liste des dossiers partagés */
    @Watch('user.Folders', { deep: true, immediate: true })
    private onUserFolderChanged(){
        if(!CompareUtils.EqualsParams(this.userForCompare.Folders, this.user.Folders))
            this.isFolderChanged = true;
        //this.isFolderChanged = !this.equalsParams(this.userForCompare.Folders, this.user.Folders);
    }
    //#endregion CHANGE TRACKING

    /** 
     * Contruit un générique tree à partir d'un TreeFolderInformation récupère depuis ServerSide.
     * @param folderTree Arbre à initialiser
     */
	private InitFolderTree(folderTree: TreeFolderInformation){
        let resultFolderTree : TreeFolderInformation = {
            Count: folderTree.Count,
            CurrentSelectedNode: undefined,
            Nodes: [...folderTree.Nodes],
            FlatNodes: {},
            SelectedNodes: [],
        }
        
        /** Initialisation des noeuds de l'arbre */
        for(let node of resultFolderTree.Nodes) {
            this.InitFolderNodes(resultFolderTree, node as TreeFolderNodeInformation);
        }

        Vue.set(this, 'folderTree', resultFolderTree);
    }

    /** 
     * Contruit les noeuds de l'arborescence à partir des TreeFolderNodeInformation.
     * @param root Métadonnées de l'arbre et racine.
     * @param node Noeud à initialiser.
     * @param [parentNode=undefined] Nœud père du nœud à initialiser ou undifined.
     */
	private InitFolderNodes(root: GenericTree, node: TreeFolderNodeInformation, parentNode: GenericTreeNode = undefined): boolean{
        /** Si le node n'est pas défini l'initialisation s'arrête là */
        if(!node) return;

        root.FlatNodes[node.Id] = node;

        /** Initialisation forcée, car la sérialisation inverse écrase les propriétés. */
        Vue.set(node, 'Disabled', false);
        Vue.set(node, 'Forced', false);
        Vue.set(node, 'Indeterminate', false);
        Vue.set(node, 'NestedChecked', false);
        Vue.set(node, 'WithCheckBox', true);
        Vue.set(node, 'Checked', false);
        Vue.set(node, 'CheckLeader', false);
        Vue.set(node, 'HiddenCaret', false);
        Vue.set(node, 'ChildNodes', []);
        Vue.set(node, 'RightBound', node.RightBound);
        Vue.set(node, 'LeftBound', node.LeftBound);

        Vue.set(node, "FatherNode", parentNode)
        /** Filtre d'origine sur les nœuds récupéré sur la v2 (pas de nœud patrimoine et par du dossier commun initial) */
        Vue.set(node, 'Hidden', !(node.Type && node.Type.toString() !== "0" && node.Id.toString() !== "1"));
        Vue.set(node, 'ObjectType', node.Type.toString());
        Vue.set(node, 'Icon', "folder-symlink-fill");
        Vue.set(node, 'Location', ( parentNode ? parentNode.Location + "/" : "" ) + node.Name);

        let expand = false;
        /** Initialisation des enfants */
        for(let childNode of node.Nodes){
            node.ChildNodes.push(childNode);
            expand = this.InitFolderNodes(root, childNode as TreeFolderNodeInformation, node) || expand;
        }

        /** Déplie les noeuds parents qui ont pour succésseur un noeud selectionné */
        Vue.set(node, 'Expanded', expand);

        /** Selection des répetoires présent dans la liste user */
        if(this.user.Folders.includes(node.Id.toString())){
            Vue.set(node, 'Checked', true);
            root.SelectedNodes.push(node);
            root.CurrentSelectedNode = node;
            return true;
        }
        return false;
	}

    //#region SIZING DU FORMULAIRE
    // Propriétés pour éviter les copier/coller redondant en cas d'ajustement
    /** Taille des labels */
    private sizeOfLabel : number = 4;
    /** Taille des inputs */
    private sizeOfInput : number = 8;
    //#endregion SIZING DU FORMULAIRE

    /**
     * Calcule l'état de validation de l'input à partir 
     * des informations de validation.
     * @param valid Indicateur de validation de l'input remonté par vee-validate 
     * @param changed Indicateur permettant de savoir si le champs est en cour d'utilisation
     */
    private getState( valid: boolean , changed: boolean, validated: boolean){
        if(validated){
            return changed ? valid : false;
        }else{
            return changed ? valid : null;
        }
    }

    /**
     * Methode exécutée lors de la soumission du formulaire après validation
     * Si l'on arrive ici, c'est que le formulaire est validé. 👍
     */
    private async onSubmitSuccess() {
        console.debug("UserAddEdit - onSubmitSuccess()");

        const usrId = this.UserId;
        /** @todo Pattern STATE ? */

        /** Netoyage des champ de saisie vitaux */
        this.user.Login = this.user.Login.trim();
        this.user.Email = this.user.Email.trim();

        /** Pousse le nouvel utilisateur */
        if(await this.save(this.user)){
            /** Nouvel utilisateur non basic */
            if(usrId < 0 && this.user.SsoAuthorityRef === null)
                /** Envoie un mail pour que l'utilisateur valide son inscription */
                await this.sendRegistrationEmail(this.userId, this.user);

            /** L'utilisateur passe en mode basic depuis le mode SSO */
            else if(usrId > -1 &&  this.user.SsoAuthorityRef === null && this.userForCompare.SsoAuthorityRef !== null)
                /** Envoie un mail pour que l'utilisateur valide son inscription car il retourne vers l'authentification classique*/
                await this.sendRegistrationEmail(this.userId, this.user);

            /** Nettoyer l'arborescence */
            await this.$store.dispatch(this.FilterTreeStore + "/RemoveAllPins");
            /** Indique la fin de l'ajout pour le composant parent */
            this.$emit('SaveEvent');
        }
    }

    /**
     * Methode exécuté lors de l'annulation de la soumission du formulaire
     * Si l'on arrive ici, c'est que le formulaire est annulé.
     */
    private async onSubmitCancel(){
        /** Nettoyer l'arborescence */
        await this.$store.dispatch(this.FilterTreeStore + "/RemoveAllPins");
        /** Indique l'annulation pour le composant parent. */
        this.$emit('CancelEvent');
    }
    
    /** Méthode exécute lors de l'échec de la soumission du formulaire. Affiche qu'une erreur est présente dans la saisie. */
    private async onSubmitInvalid(valid:boolean){
        if(!valid){
             /** Check si le champ email est vide */
            this.$bvToast.toast(this.$t('ErrorForm').toString(), { 
                title: this.$t('Error').toString(),
                autoHideDelay: 10000,
                variant: "danger",
                solid: true,
                appendToast: true,
                toaster: "b-toaster-top-center"
            });
        }
    }

    /** 
     * Enregistre l'utilisateur sur le serveur
     * @param user Utilisateur à pousser sur le serveur 
     */
    private async save(user: UserInformation): Promise<boolean>{
        console.debug("UserAddEdit - save()");

        this.$store.dispatch("ShowLoadingMask", this.$t('LoadingSave'));

        let result = false;

        if(this.user.AuthMode === "basic")
            this.user.SsoAuthorityRef = null;

        /** Arguments */
        const args: Dictionary<any> = {
            Token: Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "",
            Data: user
        };

        /** Pousse l'utilisateur */
        await axios.post(API_URL + "Administration/Users/Save", args).then((response) => {
            if(response) {
                /** Affichage du succes */
                this.$bvToast.toast(this.$t('UserSaved').toString(), { // "Une erreur s'est produite."
                    title: this.$t('Success').toString(),
                    autoHideDelay: 10000,
                    variant: "success",
                    solid: true,
                    appendToast: true,
                    toaster: "b-toaster-top-center"
                });
                /** Enregistrement du UserId de l'utilisateur créé. */
                this.userId = response.data;
                result = true;
            }
        }).catch(err =>{
            if (err.response && err.response.status === 409) {
                // Conflit sur le login
                this.$bvToast.toast(this.$t('LoginConflict').toString(), { // "Une erreur s'est produite."
                    title: this.$t('Error').toString(),
                    autoHideDelay: 10000,
                    variant: "danger",
                    solid: true,
                    appendToast: true,
                    toaster: "b-toaster-top-center"
                });
            }else{
                console.log(err);
                this.$bvToast.toast(this.$t('AnErrorOccured').toString(), { // "Une erreur s'est produite."
                    title: this.$t('Error').toString(),
                    autoHideDelay: 10000,
                    variant: "danger",
                    solid: true,
                    appendToast: true,
                    toaster: "b-toaster-top-center"
                });
            }
        }).finally(() => {
            this.$store.dispatch("HideLoadingMask", {});
        });
        return result;
    }

    /**
     * Envoi un mail de réinitialisation du mot de passe 
     * @param id Identifiant de l'utilisateur
     * @param user Utilisateur
     */
    private async sendRegistrationEmail(id: string, user: UserInformation, ){
        /** Check si le champ email est vide */
        if(!user.Email || user.Email === ""){
            this.$bvToast.toast(this.$t('NoEmail').toString(), { 
                title: this.$t('Error').toString(),
                autoHideDelay: 10000,
                variant: "danger",
                solid: true,
                appendToast: true,
                toaster: "b-toaster-top-center"
            });
        }else{
            /** Si le champ email n'est pas vide alors on peux procéder */

            /** Arguments de l'envoie de mail */
            const args: Dictionary<any> = {
                Token: Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "",
                Id: id,
                FrontUrl :new URL(this.$router.resolve('Registration').href, window.location.origin), /** URL de la page d'inscription */
                Type: RegistrationTypeEnum.Registration
            };

            /** Envoie du mail */
            await axios.post(API_URL + "Administration/Users/SendRegistrationMail", args).then((response) => {
                this.$bvToast.toast(`${user.Email}`, { 
                    title: this.$t('SendRegistrationLink').toString(),
                    autoHideDelay: 10000,
                    variant: "success",
                    solid: true,
                    appendToast: true,
                    toaster: "b-toaster-top-center"
                });
            }).catch(err =>{
                console.log(err);
                this.$bvToast.toast(this.$t('SendMailFailed').toString(), { 
                    title: this.$t('Error').toString(),
                    autoHideDelay: 10000,
                    variant: "danger",
                    solid: true,
                    appendToast: true,
                    toaster: "b-toaster-top-center"
                });
            })
        }
    }

    /** Indique si un champ a été mis à jour */
    private isWarnUpdatedFields: boolean = false;
    /** Indique si un champ a été mis à jour */
    private warnUpdatedFields(): boolean {
        this.isWarnUpdatedFields =  this.isFormChanged || this.isAssetChanged || this.isFolderChanged;
        return this.isWarnUpdatedFields;
    }

    /** Indique si un champ est en erreur */
    private isWarnInErrorFields: boolean = false;
    /** Indique si un champ est en erreur */
    private warnInErrorFields(): boolean {
        this.isWarnInErrorFields = this.$refs.userForm && this.$refs.userForm.errors && this.$refs.userForm.flags['validated'] && this.$refs.userForm.flags['invalid'] && Object.keys(this.$refs.userForm.errors).length > 0
        return this.isWarnInErrorFields;
    }

    /** Initialisation du composant */
    async created(){
        /** Récupération des données utilisateur... */
        this.$store.dispatch("ShowLoadingMask", this.$t('LoadingUserInformation'));   
        
        this.userId = this.UserId.toString();

        /** Demande de chargement des listes "de pays", "de languages supportés" et de "profils utilisateurs" */
        this.$store.dispatch("LoadListItems", [{ listName:'Country'}, { listName: 'Language'}, { listName:'Profiles'}, { listName: 'ObjectType'}, { listName: 'Sso'}]).then(_ =>{
            /** Listes des languages disponibles en selection  */
            this.languagesOptions = this.listItems("Language").map(p => ({
                value: p.Id,
                text: p.Label
            }));
            /** Listes des pays disponibles en selection  */
            this.countriesOptions = this.listItems("Country").map(p => ({
                value: p.Id,
                text: p.Label
            }));
            /** Listes des profiles disponibles en selection  */
            this.profilesOptions = this.listItems("Profiles").map(p => ({
                value: p.Id,
                text: p.Label
            }));
            /** Listes des serveur SSO disponible en selection */
            this.ssoOptions = this.listItems("Sso").map(p => ({
                value: p.Id,
                text: p.Label
            }));
        });

        /** Recupération de la liste des répertoires de la gestion documentaire */ 
        const args: Dictionary<any> = {};
        args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";

        await axios.post<TreeFolderInformation>(API_URL + "Administration/Users/GetFolderTree", args).then((response) => {
            if(response && response.data) {
                this.InitFolderTree(response.data);
            }
        });

        this.loadLazyParts.splice(0,this.loadLazyParts.length);

        if(this.UserId){
            /** Recupération des informations de l'utilisateurs. Réactualisé depuis le serveur */
            const args: Dictionary<any> = {};
            args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";
            args["UserId"] = this.UserId;

            const response: AxiosResponse<UserInformation,any> = await axios.post<UserInformation>(API_URL + "Administration/Users/GetUser", args)
            if(response.data) {
                this.user = response.data;
                if(this.user.SsoAuthorityRef !== null)
                    this.user.AuthMode = "sso"
                else
                    this.user.AuthMode = "basic"
                this.userForCompare = cloneDeep(response.data);
                /** Récupère les nœuds selectionnés */
                await this.$store.dispatch(this.FilterTreeStore + '/SetPins', this.user.Pins);
            }
        }

        /** Chargement long */
        this.$store.dispatch("HideLoadingMask", {}); 
    }
}

/** Informations de l'utilisateur en provenance ou à destination du serveur. */
type UserInformation = {
    FirstName: string
    LastName: string
    Civility: string
    InCompany:  string
    Company:  string
    Function:  string
    Confirmation:  string
    RegistrationDate:  string
    LastVisit:  string
    Language: string
    Comment: string
    Login: string
    Password: string
    Email: string
    TelNumber: string
    PortNumber: string
    FaxNumber: string
    Address: string
    Town: string
    PostalCode: string
    Country: string
    Profil: string
    CommonGroups: string[]
    /** Pins associées à l'utilisateur */
    Pins: Pin[],
    Folders: string[]
    AuthMode: string,
    SsoAuthorityRef: number | null
};

/** Racine de l'arborescence de gestion documentaire. */
class TreeFolderInformation extends GenericTree {
}

/** Noeud de l'arborescence de gestion documentaire. */
class TreeFolderNodeInformation extends GenericTreeNode {
    public Type: string;
    public Nodes: TreeFolderNodeInformation[];
}

</script>

<style scoped>

:deep(input[type=radio]:checked) {
        /** Change la couleur du bouton radio */
        accent-color: var(--Variant) !important;
    }
</style>